import { ApiError } from "@/types/AuthModels";
import router from "@/router/index";
import { useAuthStore } from "@/stores/auth/authStore";

/**
 * Base class containing api requests.
 */
export class ApiClient {
	private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
		const isFormData = options.body instanceof FormData;

		const headers: HeadersInit = {
			...(!isFormData && { "Content-Type": "application/json" }),
			...(options.headers as Record<string, string>),
		};

		const response = await fetch(endpoint, { ...options, headers, credentials: "include" });

		if (!response.ok) {
			if (response.status === 401) {
				const authStore = useAuthStore();
				authStore.clearUserValues();
				await router.push({ name: "login" });
				return null as T;
			}

			const error = await response.json().catch(() => null);

			if (response.status === 502) {
				throw new ApiError(response.status, "Cannot connect to the server.", error);
			}

			throw new ApiError(
				response.status,
				error?.detail ?? `Request failed with status ${response.status}`,
				error,
			);
		}

		if (response.status === 204) return undefined as T;

		return response.json();
	}

	async get<T>(endpoint: string): Promise<T> {
		return this.request<T>(endpoint, { method: "GET" });
	}

	async post<T>(endpoint: string): Promise<T>;
	async post<T>(endpoint: string, body: unknown): Promise<T>;
	async post<T>(endpoint: string, body?: unknown): Promise<T> {
		return this.request<T>(endpoint, {
			method: "POST",
			...(body !== undefined && { body: JSON.stringify(body) }),
		});
	}

	async postForm<T>(endpoint: string, formData: FormData): Promise<T> {
		return this.request<T>(endpoint, {
			method: "POST",
			body: formData,
		});
	}
}

export const apiClient = new ApiClient();
